import { useQuery } from '@tanstack/react-query'
import { apiClient, getSessionTokenHeaders } from '../client'
import type {
  LocationProgram,
  NearbyTrainingLocation,
  Program,
  StateRequirement,
  TrainingLocation
} from '../types/enrollment'
import { BASE_URL } from './shared'
import type { LocationHours } from './types'

export const useLocations = () => {
  return useQuery({
    queryKey: ['enrollment', 'locations'],
    queryFn: async () => {
      const response = await apiClient.get<TrainingLocation[]>(`${BASE_URL}/public/locations`)
      return response.data
    }
  })
}

export const useNearbyLocations = (
  latitude: number | null,
  longitude: number | null,
  radiusMiles = 100,
  limit = 100
) => {
  return useQuery({
    queryKey: ['enrollment', 'locations', 'nearby', latitude, longitude, radiusMiles, limit],
    queryFn: async () => {
      const response = await apiClient.get<NearbyTrainingLocation[]>(
        `${BASE_URL}/locations/nearby`,
        {
          params: { latitude, longitude, radiusMiles, limit }
        }
      )
      return response.data
    },
    enabled: latitude !== null && longitude !== null
  })
}

export const useSessionNearbyLocations = (
  latitude: number | null,
  longitude: number | null,
  sessionToken: string | null,
  sponsorCompanyId: string | null,
  radiusMiles = 100,
  limit = 100
) => {
  return useQuery({
    queryKey: [
      'enrollment',
      'locations',
      'nearby',
      'session',
      sessionToken,
      sponsorCompanyId,
      latitude,
      longitude,
      radiusMiles,
      limit
    ],
    queryFn: async () => {
      const response = await apiClient.get<TrainingLocation[] | NearbyTrainingLocation[]>(
        `${BASE_URL}/locations/nearby`,
        {
          params: {
            latitude,
            longitude,
            radiusMiles,
            limit,
            sponsorCompanyId: sponsorCompanyId || undefined
          },
          headers: getSessionTokenHeaders(sessionToken!)
        }
      )

      const data = response.data
      if (!Array.isArray(data)) {
        return []
      }

      return data.map(location =>
        'location' in location && location.location ? location.location : location
      ) as TrainingLocation[]
    },
    enabled: !!sessionToken && latitude !== null && longitude !== null
  })
}

export const useSessionLocations = (
  sessionToken: string | null,
  enabled: boolean,
  sponsorCompanyId: string | null
) => {
  return useQuery({
    queryKey: ['enrollment', 'locations', 'session', sessionToken, sponsorCompanyId],
    queryFn: async () => {
      const response = await apiClient.get<TrainingLocation[]>(`${BASE_URL}/locations`, {
        params: { sponsorCompanyId: sponsorCompanyId || undefined },
        headers: getSessionTokenHeaders(sessionToken!)
      })
      return response.data
    },
    enabled: enabled && !!sessionToken
  })
}

export const useLocation = (id: number | null) => {
  return useQuery({
    queryKey: ['enrollment', 'locations', id],
    queryFn: async () => {
      const response = await apiClient.get<TrainingLocation>(`${BASE_URL}/public/locations/${id}`)
      return response.data
    },
    enabled: !!id
  })
}

export const usePrograms = (
  locationExternalId: string | null,
  sessionToken?: string | null
) => {
  return useQuery({
    queryKey: ['enrollment', 'programs', locationExternalId, sessionToken],
    queryFn: async () => {
      if (sessionToken) {
        const response = await apiClient.get<LocationProgram[]>(
          `${BASE_URL}/session/${encodeURIComponent(sessionToken)}/programs`,
          {
            params: { locationExternalId },
            headers: getSessionTokenHeaders(sessionToken)
          }
        )
        return response.data
      }
      const response = await apiClient.get<LocationProgram[]>(
        `${BASE_URL}/locations/${encodeURIComponent(locationExternalId!)}/programs`
      )
      return response.data
    },
    enabled: !!locationExternalId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always'
  })
}

export const useProgram = (id: number | null) => {
  return useQuery({
    queryKey: ['enrollment', 'programs', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<Program>(`${BASE_URL}/public/programs/${id}`)
      return response.data
    },
    enabled: !!id
  })
}

export const useStateRequirements = (stateCode: string | null) => {
  return useQuery({
    queryKey: ['enrollment', 'state-requirements', stateCode],
    queryFn: async () => {
      const response = await apiClient.get<StateRequirement>(
        `${BASE_URL}/public/state-requirements/${stateCode}`
      )
      return response.data
    },
    enabled: !!stateCode
  })
}

export const useLocationHours = (locationExternalId: string | null | undefined) => {
  return useQuery({
    queryKey: ['enrollment', 'location-hours', locationExternalId],
    queryFn: async () => {
      const response = await apiClient.get<LocationHours>(`${BASE_URL}/locations/hours`, {
        params: { locationExternalId }
      })
      return response.data
    },
    enabled: !!locationExternalId,
    staleTime: 5 * 60 * 1000
  })
}
